import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { errorText, GAME_MODES, type GameMode, type GameSummary, type InviteInfo, type LeaderboardResponse, type ProfileResponse, type Tournament } from '@chess-uno/shared-protocol'
import { fetchInvite, fetchLeaderboard, fetchProfile } from '../api/competition.js'
import { fetchMyTournaments } from '../api/tournament.js'
import { tournamentName } from '../tournament-labels.js'
import { trackSignup } from '../api/analytics.js'
import { ApiError, fetchMyGames, login, logout, register, type StoredSession } from '../api/session.js'
import { disconnectSocket, getSocket, request } from '../api/socket.js'
import { useI18n } from '../i18n/index.js'
import { displayName } from '../i18n/names.js'
import { Avatar, Icon, SiteFooter, TopBar } from '../components/Chrome.js'
import { CampaignNote } from '../components/CampaignNote.js'
import { HeroArt } from '../components/Illustrations.js'
import {
  Leaderboard,
  RatingHero,
  RecentMatches,
  RivalriesList,
  RivalsList,
  TierBadge,
  TierLegend,
  useChallenge,
} from '../components/Competition.js'

/** ---------- Authentication ---------- */

export function AuthForm({
  onSession,
  intro,
  initialMode = 'login',
}: {
  onSession: (s: StoredSession) => void
  intro?: ReactNode
  /** Login by default; sign-up first for a guest arriving through an invite link. */
  initialMode?: 'register' | 'login'
}) {
  const { locale, t, path } = useI18n()
  const [mode, setMode] = useState<'register' | 'login'>(initialMode)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      if (mode === 'register') {
        const created = await register(username, password)
        trackSignup()
        onSession(created)
      } else {
        onSession(await login(username, password))
      }
    } catch (ex) {
      setErr(ex instanceof ApiError ? errorText(ex, locale) : ex instanceof Error ? ex.message : t.common.error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="auth-card" data-testid="auth-form">
      {intro}
      <div className="segmented" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'login'}
          className={mode === 'login' ? 'active' : ''}
          onClick={() => setMode('login')}
          data-testid="auth-tab-login"
        >
          {t.auth.login}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'register'}
          className={mode === 'register' ? 'active' : ''}
          onClick={() => setMode('register')}
          data-testid="auth-tab-register"
        >
          {t.auth.register}
        </button>
      </div>
      <label className="text-field">
        <span>{t.auth.username}</span>
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          maxLength={20}
          required
          autoFocus
          autoComplete="username"
          data-testid="auth-username"
        />
      </label>
      <label className="text-field">
        <span>{t.auth.password}</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={mode === 'register' ? 6 : 1}
          required
          autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
          data-testid="auth-password"
        />
      </label>
      {mode === 'register' && (
        <p className="fine">{t.auth.registerFine((label) => <Link to={path('/confidentialite')}>{label}</Link>)}</p>
      )}
      <button type="submit" className="primary block" disabled={busy} data-testid="auth-submit">
        {mode === 'register' ? t.auth.submitRegister : t.auth.submitLogin}
      </button>
      {err && (
        <p className="error" data-testid="auth-error">
          {err}
        </p>
      )}
    </form>
  )
}

/** ---------- Home ---------- */

function PlayAction({ to, icon, title, detail, testId }: { to: string; icon: Parameters<typeof Icon>[0]['name']; title: string; detail: string; testId: string }) {
  return (
    <Link to={to} className="play-row" data-testid={testId}>
      <span className="play-icon">
        <Icon name={icon} />
      </span>
      <span className="play-text">
        <span className="play-title">{title}</span>
        <span className="play-detail">{detail}</span>
      </span>
      <Icon name="chevron" size={14} />
    </Link>
  )
}

function Panel({ title, action, children, className = '' }: { title: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      <header className="card-head">
        <h2>{title}</h2>
        {action}
      </header>
      {children}
    </section>
  )
}

export function HomePage({
  session,
  onSession,
}: {
  session: StoredSession | null
  onSession: (s: StoredSession | null) => void
}) {
  const [games, setGames] = useState<GameSummary[]>([])
  const [tournaments, setTournaments] = useState<Tournament[]>([])
  // undefined while loading, null if the request failed.
  const [board, setBoard] = useState<LeaderboardResponse | null | undefined>(undefined)
  const [profile, setProfile] = useState<ProfileResponse | null | undefined>(undefined)
  const { challenge, error: challengeError } = useChallenge(session)
  const { locale, t, path } = useI18n()

  useEffect(() => {
    void fetchLeaderboard().then(setBoard)
    if (!session) return
    void fetchMyGames(session).then((list) => {
      if (list === null) onSession(null) // session unknown to the server (revoked, data wiped)
      else setGames(list)
    })
    void fetchProfile(session.name, session).then(setProfile)
    void fetchMyTournaments(session).then(setTournaments)
  }, [session, onSession])

  if (!session) {
    return (
      <div className="app-shell">
        <TopBar session={null} />
        <CampaignNote />
        <main>
          <div className="landing">
            <section className="landing-copy">
              <p className="eyebrow">{t.landing.eyebrow}</p>
              <h1>
                {t.landing.title}
                <br />
                <em>{t.landing.titleEm}</em>
              </h1>
              <p className="lead">{t.landing.lead}</p>
              <AuthForm onSession={onSession} />
              <Link to={path('/rules')} className="text-link" data-testid="menu-rules">
                {t.landing.readRules} <Icon name="chevron" size={12} />
              </Link>
            </section>
            <aside className="landing-side">
              <HeroArt />
              <section className="card landing-board">
                <header className="card-head">
                  <h2>{t.landing.leaderboard}</h2>
                </header>
                <Leaderboard entries={(board?.players ?? []).slice(0, 5)} compact loading={board === undefined} />
              </section>
            </aside>
          </div>
          <section className="landing-explainer" aria-labelledby="how-it-works">
            <div>
              <p className="eyebrow">{t.landing.howEyebrow}</p>
              <h2 id="how-it-works">{t.landing.howTitle}</h2>
              <p>{t.landing.howP1}</p>
              <p>{t.landing.howP2}</p>
              <Link to={path('/rules')} className="text-link">
                {t.landing.discoverRules} <Icon name="chevron" size={12} />
              </Link>
            </div>
            <div>
              <p className="eyebrow">{t.landing.playEyebrow}</p>
              <h2>{t.landing.playTitle}</h2>
              <p>{t.landing.playP1}</p>
              <p>{t.landing.playP2}</p>
            </div>
          </section>
          <section className="landing-faq" aria-labelledby="faq">
            <p className="eyebrow">{t.landing.faqEyebrow}</p>
            <h2 id="faq">{t.landing.faqTitle}</h2>
            <div className="faq-list">
              {t.landing.faq.map((item) => (
                <details key={item.q}>
                  <summary>
                    <h3>{item.q}</h3>
                    <Icon name="chevron" size={14} />
                  </summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </section>
        </main>
        <SiteFooter />
      </div>
    )
  }

  const doLogout = () => {
    void logout(session)
    disconnectSocket()
    onSession(null)
  }
  const greeting = t.home.greeting(new Date().getHours())

  return (
    <div className="app-shell">
      <TopBar session={session} rating={profile?.player.rating} onLogout={doLogout} />
      <CampaignNote />
      <main className="dashboard">
        <section className="dash-hero">
          <div className="dash-intro">
            <h1 className="greeting">
              {greeting}, <em>{session.name}</em>
            </h1>
            {profile ? (
              <RatingHero entry={profile.player} rankedPlayers={profile.rankedPlayers} />
            ) : profile === undefined ? (
              <div className="rating-hero skeleton" aria-hidden="true" />
            ) : null}
          </div>

          <aside className="play-card" aria-label={t.home.play}>
            <p className="eyebrow">{t.home.play}</p>
            <Link to="/queue" className="play-primary" data-testid="menu-queue">
              <span>
                <span className="play-primary-title">{t.home.ranked}</span>
                <span className="play-primary-detail">{t.home.rankedDetail}</span>
              </span>
              <Icon name="bolt" size={18} />
            </Link>
            <div className="play-list">
              <PlayAction to="/create" icon="link" title={t.home.challengeFriend} detail={t.home.challengeFriendDetail} testId="menu-create" />
              <PlayAction to="/join" icon="enter" title={t.home.join} detail={t.home.joinDetail} testId="menu-join" />
              <PlayAction to="/tournament" icon="trophy" title={t.home.tournament} detail={t.home.tournamentDetail} testId="menu-tournament" />
              <PlayAction to="/bot" icon="cpu" title={t.home.training} detail={t.home.trainingDetail} testId="menu-bot" />
            </div>
            <Link to={path('/rules')} className="text-link" data-testid="menu-rules">
              {t.home.rules} <Icon name="chevron" size={12} />
            </Link>
          </aside>
        </section>

        {challengeError && <p className="error">{challengeError}</p>}

        {(games.length > 0 || tournaments.length > 0) && (
          <section className="resume">
            {tournaments.map((m) => (
              <Link key={m.code} to={`/tournament/${m.code}`} className="resume-row" data-testid={`resume-tournament-${m.code}`}>
                <span className={`live-dot ${m.status === 'running' ? 'active' : 'waiting'}`} />
                <span>{(m.status === 'running' ? t.home.tournamentOngoing : t.home.tournamentOpen)(tournamentName(m, t))}</span>
                <span className="resume-cta">
                  {t.home.resume} <Icon name="chevron" size={12} />
                </span>
              </Link>
            ))}
            {games.map((g) => {
              const opponent = g.players.find((p) => p.id !== session.playerId)
              return (
                <Link key={g.gameId} to={`/game/${g.gameId}`} className="resume-row">
                  <span className={`live-dot ${g.status}`} />
                  <span>
                    {g.status === 'waiting' ? t.home.waitingGame : t.home.ongoingAgainst(opponent ? displayName(opponent, locale) : '—')}
                  </span>
                  <span className="resume-cta">
                    {t.home.resume} <Icon name="chevron" size={12} />
                  </span>
                </Link>
              )
            })}
          </section>
        )}

        <div className="dash-grid">
          <div className="dash-main">
            <Panel title={t.home.leaderboard} className="dash-leaderboard" action={<TierLegend />}>
              <div className="scroll-list scroll-list-lb">
                <Leaderboard
                  entries={board?.players ?? []}
                  meId={session.playerId}
                  onChallenge={(name) => void challenge(name)}
                  loading={board === undefined}
                />
              </div>
            </Panel>
            <Panel title={t.home.recentResults}>
              <RecentMatches matches={(board?.recent ?? []).slice(0, 6)} loading={board === undefined} />
            </Panel>
          </div>
          <div className="dash-side">
            <Panel title={t.home.headToHead}>
              <div className="scroll-list">
                <RivalsList rivals={profile?.rivals ?? []} onChallenge={(name) => void challenge(name)} loading={profile === undefined} />
              </div>
            </Panel>
            <Panel title={t.home.rivalries}>
              <div className="scroll-list">
                <RivalriesList rivalries={board?.rivalries ?? []} loading={board === undefined} />
              </div>
            </Panel>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}

/** ---------- Join ---------- */

export function JoinGamePage({ session }: { session: StoredSession }) {
  const navigate = useNavigate()
  const { code: codeParam } = useParams()
  const [code, setCode] = useState(codeParam ?? '')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const { locale, t, path } = useI18n()

  async function join(c: string) {
    setBusy(true)
    const r = await request(getSocket(session), 'game:join', { inviteCode: c.trim().toUpperCase() })
    setBusy(false)
    if (r.ok) navigate(`/game/${r.data.gameId}`)
    else setErr(errorText(r.error, locale))
  }

  useEffect(() => {
    if (codeParam) void join(codeParam)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeParam])

  return (
    <div className="app-shell">
      <TopBar session={session} />
      <main className="narrow-page">
        <Link to={path('/')} className="back-link">
          <Icon name="arrow-left" size={14} /> {t.common.back}
        </Link>
        <header className="page-head">
          <p className="eyebrow">{t.join.eyebrow}</p>
          <h1>{t.join.title}</h1>
          <p className="lead">{t.join.lead}</p>
        </header>
        <form
          className="card join-form"
          onSubmit={(e) => {
            e.preventDefault()
            void join(code)
          }}
        >
          <label className="text-field">
            <span>{t.join.code}</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              className="code-input"
              placeholder="ABCD2345"
              autoComplete="off"
              data-testid="join-code"
            />
          </label>
          <button type="submit" className="primary block" disabled={busy} data-testid="join-submit">
            {t.join.submit}
          </button>
          {err && <p className="error">{err}</p>}
        </form>
      </main>
    </div>
  )
}

/** ---------- Invite received, without an account ---------- */

/** Landing page of an invite link: who challenges you, then one-step sign-up. */
export function InviteLanding({ onSession }: { onSession: (s: StoredSession) => void }) {
  const { code = '' } = useParams()
  const [invite, setInvite] = useState<InviteInfo | null>(null)
  const { t } = useI18n()
  useEffect(() => {
    if (code) void fetchInvite(code).then(setInvite)
  }, [code])
  const intro = invite ? (
    <div className="invite-hero" data-testid="invite-hero">
      <Avatar name={invite.hostName} size={44} />
      <p className="invite-title">{t.invite.challengesYou(invite.hostName)}</p>
      <p className="invite-meta">
        <TierBadge rating={invite.hostRating} /> {invite.hostRating} Elo
      </p>
    </div>
  ) : (
    <div className="invite-hero">
      <p className="invite-title">{t.invite.invited}</p>
    </div>
  )
  return (
    <div className="app-shell">
      <TopBar session={null} />
      <main className="narrow-page">
        <AuthForm onSession={onSession} intro={intro} initialMode="register" />
      </main>
    </div>
  )
}

/** Protected page without a session: log in right there, then the requested page shows. */
export function AuthGate({ onSession }: { onSession: (s: StoredSession) => void }) {
  const { t } = useI18n()
  return (
    <div className="app-shell">
      <TopBar session={null} />
      <main className="narrow-page">
        <header className="page-head">
          <h1>{t.gate.title}</h1>
          <p className="lead">{t.gate.lead}</p>
        </header>
        <AuthForm onSession={onSession} />
      </main>
    </div>
  )
}

/** ---------- Quick match ---------- */

export function QueuePage({ session }: { session: StoredSession }) {
  const { t, path } = useI18n()
  const [mode, setMode] = useState<GameMode | null>(null)
  if (!mode) {
    return (
      <div className="app-shell">
        <TopBar session={session} />
        <main className="narrow-page">
          <Link to={path('/')} className="back-link">
            <Icon name="arrow-left" size={14} /> {t.common.back}
          </Link>
          <header className="page-head">
            <p className="eyebrow">{t.queue.eyebrow}</p>
            <h1>{t.queue.chooseTitle}</h1>
            <p className="lead">{t.queue.chooseLead}</p>
          </header>
          <div className="mode-picks">
            {GAME_MODES.map((m) => (
              <button key={m} type="button" className="mode-pick" onClick={() => setMode(m)} data-testid={`queue-mode-${m}`}>
                <span className="mode-pick-text">
                  <span className="mode-pick-title">{t.modes[m].name}</span>
                  <span className="mode-pick-tag">{t.modes[m].tagline}</span>
                  <span className="mode-pick-desc">{t.modes[m].desc}</span>
                </span>
                <Icon name="chevron" size={14} />
              </button>
            ))}
          </div>
        </main>
      </div>
    )
  }
  return <QueueSearch session={session} mode={mode} onChangeMode={() => setMode(null)} />
}

/** Waiting in the ranked queue of a mode (leaving the page leaves the queue). */
function QueueSearch({ session, mode, onChangeMode }: { session: StoredSession; mode: GameMode; onChangeMode: () => void }) {
  const navigate = useNavigate()
  const { locale, t, path } = useI18n()
  const [failure, setFailure] = useState<string | null>(null)
  useEffect(() => {
    const socket = getSocket(session)
    const onMatched = ({ gameId }: { gameId: string }) => navigate(`/game/${gameId}`)
    socket.on('queue:matched', onMatched)
    void request(socket, 'queue:join', { mode }).then((r) => {
      if (!r.ok) setFailure(errorText(r.error, locale))
      else if (r.data.gameId) navigate(`/game/${r.data.gameId}`)
    })
    return () => {
      socket.off('queue:matched', onMatched)
      void request(socket, 'queue:leave', {})
    }
    // The language is read once, when joining the queue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, navigate, mode])
  return (
    <div className="app-shell">
      <TopBar session={session} />
      <main className="narrow-page queue-page">
        <span className="searching" aria-hidden="true" />
        <p className="eyebrow">
          {t.queue.eyebrow} · {t.modes[mode].name}
        </p>
        <h1 data-testid="queue-status">{failure ?? t.queue.searching}</h1>
        <p className="lead">{t.queue.lead}</p>
        <div className="queue-actions">
          <button type="button" className="ghost" onClick={onChangeMode}>
            {t.queue.changeMode}
          </button>
          <Link to={path('/')} className="ghost button">
            {t.common.cancel}
          </Link>
        </div>
      </main>
    </div>
  )
}
